/**
 * PDP Product Info Tabs: switches panels using aria-controls.
 * Adapted from TabbedProductCollectionsComponent for the PDP info tab section.
 */
class PdpInfoTabsComponent extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const tabStrip = this.querySelector('.pdp-info-tabs__tablist');
    const panelsRoot = this.querySelector('.pdp-info-tabs__panels');
    this.#tabs = /** @type {HTMLButtonElement[]} */ (
      tabStrip ? Array.from(tabStrip.querySelectorAll('[role="tab"]')) : []
    );
    this.#panels = panelsRoot ? Array.from(panelsRoot.querySelectorAll('[role="tabpanel"]')) : [];

    if (!this.#tabs.length) return;

    for (const [i, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.#activateByTab(tab), { signal });
      tab.addEventListener('keydown', (e) => this.#onTabKeydown(e, i), { signal });
    }

    const selected = this.#tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    this.#activateByTab(selected ?? this.#tabs[0], false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} focusTab
   */
  #activateByTab(tab, focusTab = true) {
    const panelId = tab.getAttribute('aria-controls');
    const activePanel =
      panelId && document.getElementById(panelId) instanceof HTMLElement
        ? /** @type {HTMLElement} */ (document.getElementById(panelId))
        : null;

    if (!activePanel || !this.contains(activePanel)) return;

    for (const t of this.#tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.#panels) {
      if (panel === activePanel) {
        panel.removeAttribute('hidden');
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('hidden', '');
        panel.setAttribute('inert', '');
      }
    }

    if (focusTab) tab.focus();
  }

  /**
   * @param {KeyboardEvent} e
   * @param {number} index
   */
  #onTabKeydown(e, index) {
    let next = index;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      next = index <= 0 ? this.#tabs.length - 1 : index - 1;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next = index >= this.#tabs.length - 1 ? 0 : index + 1;
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = this.#tabs.length - 1;
    } else {
      return;
    }

    const tab = this.#tabs[next];
    if (tab) this.#activateByTab(tab, true);
  }
}

if (!customElements.get('pdp-info-tabs-component')) {
  customElements.define('pdp-info-tabs-component', PdpInfoTabsComponent);
}
